import { DEEP_LINK_SCHEME, appDeepLinkUrl, parseDeepLinkRoute } from "@xop/dashboard-shared";

import { env } from "../../lib/env.js";

// The desktop deep-link scheme + path helpers now live in `@xop/dashboard-shared`
// (`deepLink.ts`) so the dashboard SERVER can build the same `xopstaff://` links
// for its notification / share emails. Re-exported here so the many web call
// sites keep importing them from this module unchanged.
export { DEEP_LINK_SCHEME, appDeepLinkUrl, parseDeepLinkRoute };

/**
 * Share-link primitives for **in-app staff records** — a CRM prospect/customer
 * or a sales-agent lead. These links open a route inside the dashboard
 * (`/crm/prospects/:id`, `/sales/leads/:id`), which only logged-in staff can
 * reach, so the "public" host here is the STAFF dashboard
 * (`crm.xopnetworks.com`), NOT the customer portal.
 *
 * The whole point: a "Copy link" / "Share via email" target must be a stable,
 * externally-reachable URL — never `window.location.origin`, which is
 * `tauri://localhost` inside the desktop shell and `localhost:5174` in dev.
 * That was the `tauri://localhost/crm/prospects/385` bug. This mirrors the
 * customer-portal equivalent in `pages/documentation/shareLinks.ts`
 * (`publicPortalOrigin`) — same discipline, different destination host.
 *
 * Relative imports only (no `@/` alias) so this typechecks identically under
 * the dashboard tsconfig AND the CRM sub-app tsconfig (which remaps `@/`) — it
 * is imported from both `apps/web/src/crm/**` and `apps/web/src/pages/**`.
 *
 * Pure URL/encoding logic, unit-tested in `appShareLinks.test.ts`.
 */

/** Known-good production dashboard origin — the loopback-proof fallback for any
 * staff deep-link that leaves the app. Mirrors the doc share's
 * `PUBLIC_PORTAL_FALLBACK` and the CRM's `PUBLIC_ASSET_HOST`. */
const DASHBOARD_PUBLIC_FALLBACK = "https://crm.xopnetworks.com";

/** True for an absolute `http(s)://` origin that a *recipient* could open —
 * i.e. not loopback. Anything else (a blank value, a `tauri://` origin, a
 * `localhost` / `127.0.0.1` dev host) is rejected in favour of the public
 * fallback, so an externally-shared link is never built against a host only the
 * sender's machine can reach. */
function isPublicHttpOrigin(value: string): boolean {
  return (
    /^https?:\/\//i.test(value) &&
    !value.includes("localhost") &&
    !value.includes("127.0.0.1")
  );
}

/** Origin (scheme + host) of the staff dashboard, with no trailing slash, for
 * links that leave the app. Resolution order:
 *  1. an explicitly-configured `VITE_DASHBOARD_WEB_URL`, when it is a real
 *     public host (lets a non-standard deployment override the host);
 *  2. otherwise the live `window.location.origin`, when it is already a public
 *     http(s) host — the deployed web app IS `crm.xopnetworks.com`, so the
 *     shared link auto-tracks whatever hostname served the SPA;
 *  3. otherwise the known-good prod fallback (the desktop `tauri://` shell and
 *     local dev both land here). */
export function dashboardPublicOrigin(): string {
  const configured = (env.dashboardWebUrl ?? "").trim();
  if (configured && isPublicHttpOrigin(configured)) return stripTrailingSlash(configured);

  if (typeof window !== "undefined") {
    const origin = window.location?.origin ?? "";
    if (isPublicHttpOrigin(origin)) return stripTrailingSlash(origin);
  }

  return DASHBOARD_PUBLIC_FALLBACK;
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, "");
}

/** Absolute, externally-reachable URL for an in-app deep-link PATH (e.g.
 * `/crm/prospects/385`). Built from {@link dashboardPublicOrigin}, never
 * `window.location` — so the same link is produced from the web app, the
 * desktop shell, and dev. */
export function appAbsoluteUrl(path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${dashboardPublicOrigin()}${clean}`;
}

/** Build a `mailto:` href that opens the user's default mail app prefilled with
 * a subject and body. Generic so every share surface encodes identically. */
export function buildMailtoHref({ subject, body }: { subject: string; body: string }): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Open a `mailto:` href via a transient anchor click — opens the default mail
 * client and (unlike `window.open`) leaves no blank tab behind. No-op outside
 * the browser. Shared by the doc share and the entity share so there is ONE
 * mail-open code path.
 */
export function triggerMailto(href: string): void {
  if (typeof document === "undefined") return;
  const a = document.createElement("a");
  a.href = href;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Copy text to the clipboard. Resolves `true` on success, `false` when the
 * Clipboard API is unavailable (e.g. an insecure context) or the write throws —
 * the caller decides whether to toast. */
export async function copyToClipboard(text: string): Promise<boolean> {
  const clip = typeof navigator !== "undefined" ? navigator.clipboard : undefined;
  if (!clip?.writeText) return false;
  try {
    await clip.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Whether the platform offers a native OS share sheet (mobile web + some
 * desktop browsers). The Tauri webview on macOS/Linux does not, so the "Share
 * to app…" item is shown only when this is true. */
export function canNativeShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** Invoke the native OS share sheet so the link can be sent to any installed
 * app (Messages, Mail, Slack, …). Resolves `false` when unsupported or the
 * share is cancelled/fails, so the caller can fall back to copy-link. */
export async function triggerNativeShare(data: {
  title?: string;
  text?: string;
  url: string;
}): Promise<boolean> {
  if (!canNativeShare()) return false;
  try {
    await navigator.share(data);
    return true;
  } catch {
    return false;
  }
}
